/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { SignalStoreFeature, signalStoreFeature, withState, withMethods, EmptyFeatureResult } from '@ngrx/signals';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { identity, takeUntil, tap } from 'rxjs';
import { inject } from '@angular/core';

import { IConfirmModalContent, IConfirmModalResult } from '../../models';
import { ConfirmModalComponent } from '../../components';
import { AppConstants } from '../../config';
import { ActionType } from '../../enums';

export interface ConfirmationResult {
	modalRef: NgbModalRef
	promise: Promise<IConfirmModalResult>
}

export type ConfirmationMethods = {
	confirm: (
		content: IConfirmModalContent,
		answer?: (result: IConfirmModalResult) => void,
		reject?: (result: IConfirmModalResult) => void,
	) => ConfirmationResult,
	dismiss: () => void;
}

export function withConfirmationService<_>(): SignalStoreFeature<
	EmptyFeatureResult,
	EmptyFeatureResult & {
		methods: ConfirmationMethods;
	}
>;
export function withConfirmationService<_>(): SignalStoreFeature {
	return signalStoreFeature(
		withState({}),
		withMethods((store: any) => {
			// it's already there, avoid the annoying warning
			if (store.confirm) return {};

			const modalService = inject(NgbModal);
			return {
				confirm: (
					content: IConfirmModalContent,
					answer: (result: IConfirmModalResult) => void = identity,
					reject: (result: IConfirmModalResult) => void = identity,
				) => {
					const modalRef = modalService.open(ConfirmModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
					const promise = new Promise((res, rej) => {
						const modal = (modalRef.componentInstance as ConfirmModalComponent);
						modal.contentConfig = content;
						modal.answer.pipe(
							tap(result => {
								if (result.action === ActionType.VALIDATE) {
									res(result)
									answer(result)
								} else {
									rej(result)
									reject(result)
								}
							}),
							takeUntil(modal.destroySubscriptions$),
						).subscribe();
					})
					return { modalRef, promise }
				},
				dismiss: _ => modalService.dismissAll(),
			}
		}),
	);
}
